import { useTranslation } from 'react-i18next';

import { MarkdownText } from '../markdown/MarkdownText';

import { CITATION_TOKEN } from './chatSources';
import { CitationChip } from './CitationChip';
import {
  composerSafeAnchorHandlers,
  composerSafeButtonHandlers,
} from './composerSafeButtonHandlers';
import { CITATION_LINK_PREFIX } from './constants';
import {
  isInAppAssistantHref,
  toInAppAssistantLocation,
} from './inAppAssistantHref';
import type { AiAssistantMessage } from './types';

import styles from './CitationText.module.scss';

type CitationTextProps = {
  message: AiAssistantMessage;
  onJumpToSource: (message: AiAssistantMessage, number: number) => void;
  /**
   * In-app markdown links (same-origin or portal page paths) call this instead
   * of a full document load.
   */
  onNavigate?: (to: string) => void;
};

type MarkdownNode = {
  children?: MarkdownNode[];
  type: string;
  url?: string;
  value?: string;
};

function createCitationRemarkPlugin(citationNumbers: readonly number[]) {
  return () => (tree: MarkdownNode) => {
    function transformTextNodes(node: MarkdownNode) {
      if (!node.children) {
        return;
      }

      node.children = node.children.flatMap<MarkdownNode>((child) => {
        if (child.type === 'code' || child.type === 'inlineCode') {
          return child;
        }
        if (child.type !== 'text' || child.value == null) {
          if (child.type !== 'link') {
            transformTextNodes(child);
          }
          return child;
        }

        return child.value.split(CITATION_TOKEN).map<MarkdownNode>((part) => {
          const match = /^\[\[(\d+)]]$/.exec(part);
          const number = match ? Number(match[1]) : 0;
          if (!citationNumbers.includes(number)) {
            return { type: 'text', value: part };
          }

          return {
            type: 'link',
            url: `${CITATION_LINK_PREFIX}${number}`,
            children: [{ type: 'text', value: String(number) }],
          };
        });
      });
    }

    transformTextNodes(tree);
  };
}

/**
 * Renders assistant reply text with inline `[[n]]` citation tokens turned into
 * numbered chips. Each `n` is 1-based and maps to `message.sources[n - 1]`;
 * clicking a chip calls {@link CitationTextProps.onJumpToSource}. The consumer
 * decides the target (previewer vs sources panel).
 *
 * Assistant replies are rendered as GitHub-flavored Markdown via
 * {@link MarkdownText}. Out-of-range citation tokens are left as literal
 * `[[n]]` text.
 *
 * Chips use {@link composerSafeButtonHandlers} so a composer blur/shrink cannot
 * swallow the interaction; keyboard activation still uses `click` (`detail === 0`).
 */
export function CitationText({
  message,
  onJumpToSource,
  onNavigate,
}: CitationTextProps) {
  const { t } = useTranslation();
  const sources = message.sources ?? [];
  const citationNumbers = sources.map(
    (source, index) => source.citationNumber ?? index + 1
  );
  if (message.sender !== 'assistant') {
    return message.text;
  }

  return (
    <MarkdownText
      components={{
        a: ({ children, href }) => {
          const number = href?.startsWith(CITATION_LINK_PREFIX)
            ? Number(href.slice(CITATION_LINK_PREFIX.length))
            : null;
          if (number == null || !Number.isInteger(number)) {
            if (onNavigate && href && isInAppAssistantHref(href)) {
              const inAppLocation = toInAppAssistantLocation(href);
              return (
                <a
                  href={href}
                  {...composerSafeAnchorHandlers(() =>
                    onNavigate(inAppLocation)
                  )}
                >
                  {children}
                </a>
              );
            }

            return <a href={href}>{children}</a>;
          }

          return (
            <button
              className={styles['citation-chip-button']}
              data-testid={`ai-assistant-citation-${message.id}-${number}`}
              {...composerSafeButtonHandlers(() =>
                onJumpToSource(message, number)
              )}
              title={t('app.aiAssistant.jumpToSource', {
                index: String(number),
              })}
              type="button"
            >
              <CitationChip>{children}</CitationChip>
            </button>
          );
        },
      }}
      remarkPlugins={
        citationNumbers.length > 0
          ? [createCitationRemarkPlugin(citationNumbers)]
          : undefined
      }
    >
      {message.text}
    </MarkdownText>
  );
}
